import { Link, useParams, useSearchParams } from "react-router-dom";
import { css } from "styled-system/css";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { FadeIn } from "@/components/ui/animated";

type ConfirmJson = {
  appointment: {
    serviceName: string;
    professionalName: string;
    depositStatus: string | null;
    status: string;
    paymentStatus: string;
  } | null;
};

export function BookConfirmPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sp] = useSearchParams();
  const ref = sp.get("ref");
  const payOnSite = sp.get("payOnSite") === "1";
  const [detail, setDetail] = useState<ConfirmJson["appointment"] | "loading">("loading");

  useEffect(() => {
    if (!slug || !ref) {
      setDetail(null);
      return;
    }
    const u = new URL("/api/public/appointment-confirm", window.location.origin);
    u.searchParams.set("ref", ref);
    u.searchParams.set("slug", slug);
    let cancelled = false;
    void fetch(u.toString())
      .then((r) => r.json())
      .then((d: ConfirmJson) => {
        if (!cancelled) setDetail(d.appointment);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, ref]);

  const pendingDeposit = detail && detail.depositStatus === "pending" && detail.status === "PENDING";

  useEffect(() => {
    if (!pendingDeposit || !ref || !slug) return;
    const tick = () => {
      const u = new URL("/api/public/appointment-confirm", window.location.origin);
      u.searchParams.set("ref", ref);
      u.searchParams.set("slug", slug);
      void fetch(u.toString())
        .then((r) => r.json())
        .then((d: ConfirmJson) => {
          if (d.appointment?.status === "CONFIRMED") {
            setDetail(d.appointment);
          }
        })
        .catch(() => {});
    };
    const id = window.setInterval(tick, 4000);
    return () => window.clearInterval(id);
  }, [pendingDeposit, ref, slug]);

  if (detail === "loading") {
    return (
      <div className={css({ minH: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: "6", bg: "bg.muted" })}>
        <div className={css({ w: "100%", maxW: "420px" })}>
          <LoadingFrame variant="light" minimal minHeight={80} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={css({
        minH: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: "6",
        bg: "bg.muted",
      })}
    >
      <FadeIn>
        <Card className={css({ maxW: "md", width: "100%", textAlign: "center" })}>
        <h1 className={css({ fontSize: "2xl", fontWeight: "bold", mb: "2", color: "fg.DEFAULT" })}>
          {detail
            ? pendingDeposit
              ? "Pendiente de pago"
              : payOnSite
                ? "Reserva confirmada"
                : "¡Listo!"
            : "Turno registrado"}
        </h1>
        <p className={css({ color: "fg.muted", fontSize: "sm", mb: "6" })}>
          {detail
            ? pendingDeposit
              ? `Si ya pagaste en Mercado Pago, el turno se confirmará en segundos. También revisá tu email con el enlace de pago. ${detail.serviceName} con ${detail.professionalName}.`
              : payOnSite
                ? `Reserva para ${detail.serviceName} con ${detail.professionalName}. Abonás en el lugar al momento del turno (el profesional no habilitó pago online con Mercado Pago en esta reserva). Revisá tu email.`
                : `Reserva para ${detail.serviceName} con ${detail.professionalName}. Revisá tu email.`
            : "Si pagaste con Mercado Pago, el turno se confirmará al aprobarse el pago."}
        </p>
        <Link to={slug ? `/book/${slug}` : "/"}>
          <Button variant="secondary">Volver al inicio</Button>
        </Link>
        </Card>
      </FadeIn>
    </div>
  );
}
